import React, { useState, useEffect } from 'react';
import { GetActiveClients, GetClients } from '../Shared/Services';
import { Button, Col, message, Row, Select, Tabs, Tooltip } from 'antd';
import ClientTable from './ClientTable';
import { PlusOutlined, EditOutlined, ExportOutlined, SwapOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { iconSize } from '../Shared/UI/jsx';
import ClientForm from './Forms';
import PediPlayPageHeader from '../Shared/UI/PediplayPageHeader';
import { countyTransform, exportToCSV, uploadPdfForRecord } from '../Shared/Functions';
import DocumentFinderModal from '../Shared/Components/DocumentFinderModal';


const Clients = props => {
    const [data, setData] = useState();
    const [highlighted, setHighlighted] = useState();
    const [loading, setLoading] = useState(true);
    const [formType, setFormType] = useState();
    const [visibility, setVisibility] = useState(false);
    const [docModalVisible, setDocModalVisible] = useState(false);
    const [docType, setDocType] = useState("");
    const [activeTab, setActiveTab] = useState("0");
    const fileInputRef = React.useRef(null);

    const documentTypes = [
            "IFSP",
            "MD-Signature",
            "Initial-Assessment",
            "Initial-Arrows",
            "MHS",
            "Initial-Packet",
            "ICO",
            "Eligibility-Statement",
            "Annual-Paperwork",
            "Consent-Page",
            "Reports",
            "Referrals",
            "Other"
        ]

    useEffect(() => {
            GetClients()
                .then((res) => {
                    setData(res.data);
                    setHighlighted(res.data[0]);
            })
            .finally(() => {
                setLoading(false);
            })
    }, []);

    const handleEdit = () => {
        setFormType('edit');
        setVisibility(true);
    }

    const handleCreate = () => {
        setFormType('create');
        setVisibility(true);
    }


    const handleAfterFormSubmit = (record) => {
        setData(prev => {
            if (!prev || prev.length === 0) {
            return [record];
            }

            const exists = prev.some(c => c.id === record.id);

            return exists
            ? prev.map(c => c.id === record.id ? record : c)   // update existing
            : [...prev, record];                               // add new
        });

        setHighlighted(record);
    };


    const handleRowOnClick = record => {
        setHighlighted(record);
    }

    const handleUploadClick = () => {
        if (!highlighted) {
            message.error("Please select a client first");
            return;
        }
        if (!docType) {
            message.error("Please select a document type");
            return;
        }
        fileInputRef.current.click();
    };

    const handleFileSelected = (e) => {
        const file = e.target.files[0];
        if(file) {
            uploadPdfForRecord(highlighted, file, docType)
        }
    };

    const isActiveTab = Number(activeTab) === 0;
    const activeCount = Array.isArray(data) ? data.filter(r => r.active === true).length : 0;
    const inactiveCount = Array.isArray(data) ? data.filter(r => r.active === false).length : 0

    const filteredData = Array.isArray(data)
    ? data.filter(r => r.active === isActiveTab)
    : [];


    const extra = (
        <div style={{ textAlign: 'end' }}>
            <Tooltip title="Add" placement="top">
                <Button
                    type="link"
                    icon={<PlusOutlined style={iconSize} />}
                    onClick={() => handleCreate()}
                />
            </Tooltip>
            <Tooltip title="Edit" placement="top">
                <Button 
                    type="link"
                    icon={<EditOutlined style={iconSize} />}
                    onClick={() => handleEdit()}
                />
            </Tooltip>
            <Tooltip title="Documents" placement='top'>
                <Button
                    type='link'
                    icon={<FilePdfOutlined style={iconSize} />}
                    onClick={() => {
                        if (!highlighted) {
                            message.error("Please select a client first");
                            return;
                        }
                        setDocModalVisible(true);
                    }}
                />    
            </Tooltip>
            <Select
                placeholder="Document Type"
                value={docType}
                onChange={setDocType}
                options={documentTypes.map(d => ({ label: d, value: d }))}
                style={{ width: 180, marginRight: 8 }}
            />
            <Tooltip title="Upload PDF" placement='top'>
                <Button
                    type='link'
                    icon={<UploadOutlined style={iconSize} />}
                    onClick={handleUploadClick}
                />
                <input
                    type='file'
                    accept='application/pdf'
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelected}
                />            
            </Tooltip>    
            <Tooltip title="Export" placement='top'>
                <Button
                    type='link'
                    icon={<ExportOutlined style={iconSize} />}
                    onClick={() => exportToCSV("Clients.csv", data, {
                        exclude: ["id"],
                        transforms: {
                            county: countyTransform
                        }
                    })}
                />    
            </Tooltip>
            <ClientForm 
                formType={formType}
                setFormType={setFormType}
                visibility={visibility}
                setVisibility={setVisibility}
                highlighted={highlighted}
                handleAfterFormSubmit={handleAfterFormSubmit}
            />
            <DocumentFinderModal
                visible={docModalVisible}
                onClose={() => setDocModalVisible(false)}
                record={highlighted}
            />    
        </div>
    );

    return (
        <div>
            <Row gutter={[8,8]}>
                <Col span={24}>
                    <PediPlayPageHeader 
                        title={'Clients'}
                        props={props}
                        extra={extra}
                        loading={loading}
                    />
                </Col>
                <Col span={24}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={[
                            { key: "0", label: "Active Clients" },
                            { key: "1", label: "Inactive Clients" }
                        ]}
                    />    
                </Col>
                <Col span={24}>
                        <div
                            style={{
                                padding: "12px 16px",
                                background: "#fafafa",
                                borderRadius: 6,
                                border: "1px solid #e8e8e8",
                                marginBottom: 12,
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 16,
                                fontWeight: 500
                            }}
                        >
                            <div>Active Clients: {activeCount}</div>
                            <div>Inactive Clients: {inactiveCount}</div>
                        </div>    
                </Col>
                <Col span={24}>
                    <ClientTable key={activeTab} loading={loading} highlighted={highlighted} clients={filteredData} handleRowOnClick={handleRowOnClick}/>
                </Col>
            </Row>
        </div>
    );

}

export default Clients;